import { useEffect, useState } from "react";
import { ImageOff, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { filesApi } from "@/lib/api";
import type { MessageAttachment } from "@/lib/types";

interface CommentImagesProps {
  requestId: string;
  images: MessageAttachment[];
}

/**
 * Screenshots inside a comment. Thumbnails use the short-lived link that came
 * with the thread; the full-size view is fetched through the API instead, so
 * it still works after that link has expired.
 */
export function CommentImages({ requestId, images }: CommentImagesProps) {
  const [open, setOpen] = useState<MessageAttachment | null>(null);
  const [fullUrl, setFullUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [broken, setBroken] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open) return;
    let objectUrl: string | null = null;
    let cancelled = false;
    setFullUrl(null);
    setFailed(false);
    filesApi
      .blob(requestId, open.id)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setFullUrl(objectUrl);
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, requestId]);

  if (images.length === 0) return null;

  return (
    <>
      <div className="mt-2 flex flex-wrap gap-2">
        {images.map((img) => (
          <button
            key={img.id}
            type="button"
            onClick={() => setOpen(img)}
            className="overflow-hidden rounded-md border bg-background focus:outline-none focus:ring-2 focus:ring-ring"
            title={img.filename}
          >
            {img.url && !broken.has(img.id) ? (
              <img
                src={img.url}
                alt={img.filename}
                loading="lazy"
                className="h-28 w-auto max-w-[220px] object-cover"
                onError={() => setBroken((s) => new Set(s).add(img.id))}
              />
            ) : (
              // Link expired or unavailable — still clickable for the full view.
              <span className="flex h-28 w-36 flex-col items-center justify-center gap-1 px-2 text-xs text-muted-foreground">
                <ImageOff className="h-5 w-5" />
                <span className="truncate max-w-full">{img.filename}</span>
              </span>
            )}
          </button>
        ))}
      </div>

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="truncate">{open?.filename}</DialogTitle>
          </DialogHeader>
          <div className="flex min-h-[200px] items-center justify-center">
            {failed ? (
              <p className="text-sm text-muted-foreground">
                Couldn't load this image. Please try again.
              </p>
            ) : fullUrl ? (
              <img
                src={fullUrl}
                alt={open?.filename}
                className="max-h-[75vh] w-auto rounded"
              />
            ) : (
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
